/* Export to PDF, through the browser's print dialog: the chosen messages laid
 * out one per page in a print-only container, then `print()`. Save as PDF is
 * a destination in every desktop browser's dialog.
 *
 * **The owner's choice (2026-09-18) over a JavaScript PDF library**, which
 * either loses an HTML email's layout or rasterises it into pictures of text.
 * The dialog costs a click and gives the browser's own layout, selectable
 * text, every font the system has, and correct page breaks.
 *
 * **The body is rendered by the same function as the screen**, so what prints
 * is what was read.
 */

import { renderBody, contentIdUrls, realAttachments, fillMeta, blockedText } from "./view.js";
import { safeFileName, size, count } from "./format.js";

/** Whatever an export left behind, cleared. Set when an export is ready. */
let leftover = null;

/**
 * Prepares the messages, then asks: the print window is opened by a click on
 * the dialog, never straight after the preparation.
 *
 * **Why two steps, in every browser** (the owner, 2026-09-18): a page may open
 * the print window only straight from a click. Preparing takes a moment, and
 * Safari then asked whether to let "the web page" print; declining it meant
 * `afterprint` never came, the page stayed marked as printing, and every later
 * export was refused until a reload. A click on the dialog is always fresh, and
 * a new export always clears what the last one left, so neither can recur.
 *
 * @param {object} page  body, container, template, progress(text), dialog: {element, ready, go, cancel, backdrop}
 */
export async function printMessages(page, mailbox, ids, load = (id) => mailbox.reader.message(id)) {
  if (!ids.length) return;
  leftover?.();

  const urls = [];
  const container = page.container;
  container.replaceChildren();

  try {
    for (let i = 0; i < ids.length; i++) {
      if (ids.length > 1) page.progress(`Preparing ${count(i + 1)} of ${count(ids.length)}`);
      container.appendChild(printedMessage(page.template, await load(ids[i]), urls));
    }

    // Pictures from the message itself must be decoded before the print
    // window takes its snapshot, or they print as empty boxes.
    const images = [...container.querySelectorAll('[data-mb="print-body"]')]
      .flatMap((host) => [...host.shadowRoot.querySelectorAll("img[src]")]);
    await Promise.all(images.map((image) => image.decode().catch(() => {})));
  } catch (error) {
    page.progress(null);
    container.replaceChildren();
    for (const url of urls) URL.revokeObjectURL(url);
    throw error;
  }

  page.progress(null);

  const title = document.title;
  const { dialog } = page;

  const cleanup = () => {
    window.removeEventListener("afterprint", cleanup);
    dialog.go.onclick = dialog.cancel.onclick = dialog.backdrop.onclick = dialog.element.oncancel = null;
    if (dialog.element.open) dialog.element.close();
    delete page.body.dataset.mbPrinting;
    document.title = title;
    container.replaceChildren();
    for (const url of urls) URL.revokeObjectURL(url);
    if (leftover === cleanup) leftover = null;
  };
  leftover = cleanup;

  dialog.ready.textContent = ids.length === 1
    ? "Ready: the message is laid out for printing."
    : `Ready: ${count(ids.length)} messages, each starting on a new page.`;

  dialog.go.onclick = () => {
    dialog.element.close();
    // The title becomes the suggested file name in Chrome, Edge and Safari.
    document.title = ids.length === 1
      ? safeFileName(mailbox.items[ids[0]]?.subject)
      : safeFileName(`${mailbox.name} (${count(ids.length)} messages)`);
    page.body.dataset.mbPrinting = "";
    window.addEventListener("afterprint", cleanup);
    window.print(); // straight from this click, so no browser asks first
  };
  dialog.cancel.onclick = dialog.backdrop.onclick = cleanup;
  dialog.element.oncancel = cleanup; // Escape

  dialog.element.showModal();
  dialog.go.focus();
}

function printedMessage(template, parsed, urls) {
  const article = template.content.firstElementChild.cloneNode(true);
  const find = (name) => article.querySelector(`[data-mb="${name}"]`);

  find("print-subject").textContent = parsed.subject || "(no subject)";

  const meta = find("print-meta");
  fillMeta(meta, parsed);
  // The screen's meta classes are theme colours; on paper the template's own win.
  for (const element of meta.children) element.removeAttribute("class");

  const attachments = realAttachments(parsed);
  if (attachments.length) {
    const dt = document.createElement("dt");
    dt.textContent = "Attachments";
    const dd = document.createElement("dd");
    dd.textContent = attachments.map((a) => `${a.filename || "Unnamed"} (${size(a.content.byteLength)})`).join(", ");
    meta.append(dt, dd);
  }

  const blocked = renderBody(find("print-body"), parsed, contentIdUrls(parsed, urls));
  if (blocked) {
    const note = find("print-blocked");
    note.textContent = blockedText(blocked);
    note.classList.remove("hidden");
  }

  return article;
}
